import React from 'react';
import { FileText } from 'lucide-react';

/** Purpose: Renders upload form for provider-request shares. Inputs: props. Output: React render output (JSX). */
export default function SharedUploadView({
  ownerName,
  recordTypeOptions,
  uploadTitle,
  setUploadTitle,
  uploadType,
  setUploadType,
  uploadDate,
  setUploadDate,
  uploadDescription,
  setUploadDescription,
  analysisStatus,
  analysisMessage,
  uploadStatus,
  uploadMessage,
  onFileChange,
  onSubmit
}) {
  return (
    <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="bg-emerald-100 p-2 rounded-lg">
          <FileText className="w-5 h-5 text-emerald-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Upload Records</h2>
          <p className="text-sm text-slate-500">
            Send additional documents securely to {ownerName || 'the owner'}.
          </p>
        </div>
      </div>

      {uploadMessage && (
        <div className={`mb-4 rounded-lg px-4 py-2 text-sm ${
          uploadStatus === 'success'
            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {uploadMessage}
        </div>
      )}

      {analysisMessage && (
        <div className={`mb-4 rounded-lg px-4 py-2 text-sm ${
          analysisStatus === 'error'
            ? 'bg-amber-50 text-amber-700 border border-amber-200'
            : analysisStatus === 'analyzing'
              ? 'bg-blue-50 text-blue-700 border border-blue-200'
              : 'bg-slate-50 text-slate-700 border border-slate-200'
        }`}>
          {analysisMessage}
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Record Title (optional)</label>
          <input
            type="text"
            value={uploadTitle}
            onChange={(event) => setUploadTitle(event.target.value)}
            placeholder="e.g., Lab Results - March 2026"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Record Type</label>
            <select
              value={uploadType}
              onChange={(event) => setUploadType(event.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {recordTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Record Date</label>
            <input
              type="date"
              value={uploadDate}
              onChange={(event) => setUploadDate(event.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Description (optional)</label>
          <textarea
            value={uploadDescription}
            onChange={(event) => setUploadDescription(event.target.value)}
            placeholder="Brief note about this record"
            rows={3}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">File</label>
          <input
            type="file"
            onChange={(event) => onFileChange(event.target.files?.[0] || null)}
            className="w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            required
          />
        </div>

        <button
          type="submit"
          disabled={uploadStatus === 'uploading'}
          className="w-full md:w-auto px-6 py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition disabled:opacity-60"
        >
          {uploadStatus === 'uploading' ? 'Uploading…' : 'Upload Securely'}
        </button>
      </form>
    </section>
  );
}
